import {
  ANPD_DISCLAIMER,
  CONTENT_VERSION,
  COURSE_IDS,
  ENCARREGADO,
  ITEM_COUNT,
  PASS_CUT,
  SLIDE_COUNT,
  STORAGE_KEY,
} from './constants.js';

export * from './constants.js';

export function beatFor(n) {
  if (n <= 2) return 'hook';
  if (n <= 8) return 'lesson';
  if (n <= 10) return 'quiz';
  if (n <= 16) return 'lesson';
  if (n <= 18) return 'quiz';
  if (n <= 24) return 'lesson';
  if (n <= 27) return 'quiz';
  if (n <= 29) return 'recap';
  return 'certificate';
}

export function createLearner(name, email) {
  const n = String(name || '').trim();
  const e = String(email || '').trim().toLowerCase();
  if (!n) throw new Error('NOME_OBRIGATORIO');
  if (!/^[^\s@]+@altec\.ws$/i.test(e)) throw new Error('EMAIL_ALTEC');
  return { name: n, email: e };
}

function emptyRecord(status) {
  return {
    status,
    score: null,
    passed: false,
    certificate: null,
    passedAt: null,
    history: [],
  };
}

export function createStore(learner) {
  return {
    learner,
    records: {
      'escudo-estelar': emptyRecord('available'),
      'cofre-quantico': emptyRecord('locked'),
      'rede-nebulosa': emptyRecord('locked'),
    },
    trilhaCertificate: null,
    events: [],
  };
}

export function canStartCourse(store, courseId) {
  const rec = store.records[courseId];
  return Boolean(rec && rec.status !== 'locked');
}

export function nextCourseEnabled(store, fromCourseId) {
  const idx = COURSE_IDS.indexOf(fromCourseId);
  if (idx < 0) return false;
  if (idx === COURSE_IDS.length - 1) return Boolean(store.trilhaCertificate);
  return canStartCourse(store, COURSE_IDS[idx + 1]);
}

export function scoredItemsOf(course) {
  const items = [];
  for (const slide of course.slides) {
    if (!slide.scored) continue;
    for (const item of slide.scored.items) items.push({ slide, item });
  }
  return items;
}

export function slideForItem(course, itemId) {
  return course.slides.find((s) => s.scored?.items.some((i) => i.id === itemId));
}

export function shuffleIds(ids, rng = Math.random) {
  const a = [...ids];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export function optionOrderFor(session, slide) {
  if (!slide?.scored?.options) return [];
  if (!session.optionOrder) session.optionOrder = {};
  const key = String(slide.n);
  if (!session.optionOrder[key]) {
    session.optionOrder[key] = shuffleIds(slide.scored.options.map((o) => o.id));
  }
  return session.optionOrder[key];
}

export function orderedOptions(session, slide) {
  const order = optionOrderFor(session, slide);
  const byId = Object.fromEntries((slide.scored.options || []).map((o) => [o.id, o]));
  return order.map((id) => byId[id]).filter(Boolean);
}

export function gradeItem(item, selection) {
  if (item.kind === 'single') {
    return selection === item.correct;
  }
  const selected = new Set(Array.isArray(selection) ? selection : []);
  const must = item.mustInclude || [];
  const forbid = item.mustExclude || [];
  return must.every((id) => selected.has(id)) && forbid.every((id) => !selected.has(id));
}

function resetQuizFields(session) {
  session.pendingChoice = null;
  session.pendingMulti = [];
  session.quizSubmitted = false;
  session.feedback = null;
}

export function startSession(store, course) {
  if (!canStartCourse(store, course.id)) {
    const err = new Error('PORTAO_FECHADO');
    err.code = 'PORTAO_FECHADO';
    throw err;
  }
  store.records[course.id].status = 'in_progress';
  return {
    courseId: course.id,
    slideIndex: 0,
    answers: {},
    pendingChoice: null,
    pendingMulti: [],
    quizSubmitted: false,
    feedback: null,
    retryQueue: [],
    retryCursor: 0,
    phase: 'slides',
    retried: {},
    optionOrder: {},
  };
}

export function currentSlide(session, course) {
  if (session.phase === 'retry') {
    const itemId = session.retryQueue[session.retryCursor];
    return slideForItem(course, itemId);
  }
  return course.slides[session.slideIndex];
}

export function getView(session, course) {
  if (session.phase === 'retry-intro') {
    return {
      kind: 'retry-intro',
      missed: missedItemIds(session),
      teca: 'teca-nao',
    };
  }
  const slide = currentSlide(session, course);
  return { kind: 'slide', slide, phase: session.phase };
}

export function isQuizSlide(slide) {
  return Boolean(slide?.scored);
}

export function canAdvance(session, course) {
  const view = getView(session, course);
  if (view.kind === 'retry-intro') return false;
  const slide = view.slide;
  if (isQuizSlide(slide) && !session.quizSubmitted) return false;
  return true;
}

export function selectOption(session, optionId) {
  if (session.quizSubmitted) return;
  session.pendingChoice = optionId;
}

export function toggleMulti(session, optionId) {
  if (session.quizSubmitted) return;
  const set = new Set(session.pendingMulti);
  if (set.has(optionId)) set.delete(optionId);
  else set.add(optionId);
  session.pendingMulti = [...set];
}

export function markedChoice(session, slide) {
  if (!slide?.scored) return false;
  if (slide.scored.type === 'single') return Boolean(session.pendingChoice);
  return true;
}

export function submitQuiz(session, slide) {
  if (!slide?.scored) {
    const err = new Error('NAO_E_QUIZ');
    err.code = 'NAO_E_QUIZ';
    throw err;
  }
  if (slide.scored.type === 'single' && !session.pendingChoice) {
    const err = new Error('QUIZ_TRAVADO');
    err.code = 'QUIZ_TRAVADO';
    throw err;
  }
  const selection =
    slide.scored.type === 'single' ? session.pendingChoice : session.pendingMulti;
  const results = [];
  for (const item of slide.scored.items) {
    if (session.phase === 'retry' && !session.retryQueue.includes(item.id)) {
      continue;
    }
    const correct = gradeItem(item, selection);
    session.answers[item.id] = {
      selection,
      correct,
      slide: slide.n,
    };
    results.push({ id: item.id, correct });
  }
  session.quizSubmitted = true;
  const allCorrect = results.length > 0 && results.every((r) => r.correct);
  session.feedback = {
    correct: allCorrect,
    teca: allCorrect ? 'teca-sim' : 'teca-nao',
    text: allCorrect ? slide.scored.gabaritoCerto : slide.scored.gabaritoErrado,
    results,
  };
  return session.feedback;
}

export function scoreOf(session) {
  const ids = Object.keys(session.answers);
  const correct = ids.filter((id) => session.answers[id].correct).length;
  return { correct, total: ITEM_COUNT, ids };
}

export function hasPassed(session) {
  return scoreOf(session).correct >= PASS_CUT;
}

export function missedItemIds(session) {
  return Object.keys(session.answers)
    .filter((id) => !session.answers[id].correct)
    .sort();
}

export function startRetry(session, course) {
  const missed = missedItemIds(session);
  session.retryQueue = uniqueSlideItems(course, missed);
  session.retryCursor = 0;
  session.phase = 'retry';
  resetQuizFields(session);
  return session.retryQueue;
}

function uniqueSlideItems(course, itemIds) {
  const seenSlides = new Set();
  const ordered = [];
  for (const id of itemIds) {
    const slide = slideForItem(course, id);
    if (!slide) continue;
    const key = slide.n;
    if (seenSlides.has(key)) {
      if (!ordered.includes(id)) ordered.push(id);
      continue;
    }
    seenSlides.add(key);
    for (const item of slide.scored.items) {
      if (itemIds.includes(item.id) && !ordered.includes(item.id)) {
        ordered.push(item.id);
      }
    }
  }
  return ordered;
}

function compactRetryQueue(session, course) {
  session.retryQueue = uniqueSlideItems(course, missedItemIds(session));
}

export function enterRetryIntro(session) {
  session.phase = 'retry-intro';
  resetQuizFields(session);
}

export function advance(session, course) {
  if (!canAdvance(session, course)) {
    const err = new Error('QUIZ_TRAVADO');
    err.code = 'QUIZ_TRAVADO';
    throw err;
  }
  if (session.phase === 'retry') {
    const slide = currentSlide(session, course);
    const idsOnSlide = slide.scored.items.map((i) => i.id);
    while (
      session.retryCursor < session.retryQueue.length &&
      idsOnSlide.includes(session.retryQueue[session.retryCursor])
    ) {
      session.retryCursor += 1;
    }
    resetQuizFields(session);
    if (session.retryCursor >= session.retryQueue.length) {
      session.phase = 'slides';
      session.slideIndex = 27;
      return getView(session, course);
    }
    return getView(session, course);
  }

  const slide = course.slides[session.slideIndex];
  if (slide.n === 27 && session.phase === 'slides') {
    compactRetryQueue(session, course);
    if (session.retryQueue.length) {
      session.phase = 'retry-intro';
      resetQuizFields(session);
      return getView(session, course);
    }
  }

  if (session.slideIndex >= SLIDE_COUNT - 1) {
    return getView(session, course);
  }
  session.slideIndex += 1;
  resetQuizFields(session);
  return getView(session, course);
}

export function goToSlide(session, course, n) {
  session.phase = 'slides';
  session.slideIndex = n - 1;
  resetQuizFields(session);
  return getView(session, course);
}

function certificatePayload({ learner, course, score, issuedAt, kind }) {
  return {
    learnerName: learner.name,
    learnerEmail: learner.email,
    courseId: course?.id ?? 'trilha',
    courseTitle: course?.title ?? 'Academia LGPD Altec',
    issuedAt: issuedAt.toISOString(),
    score: score.correct,
    total: ITEM_COUNT,
    scoreLabel: `${score.correct}/${ITEM_COUNT}`,
    version: CONTENT_VERSION,
    disclaimer: ANPD_DISCLAIMER,
    encarregado: ENCARREGADO,
    kind,
    pass: true,
  };
}

export function issueCertificate(store, session, course, issuedAt = new Date()) {
  const score = scoreOf(session);
  if (score.correct < PASS_CUT || score.total !== ITEM_COUNT) {
    const err = new Error('NOTA_INSUFICIENTE');
    err.code = 'NOTA_INSUFICIENTE';
    err.score = score;
    throw err;
  }
  if (Object.keys(session.answers).length !== ITEM_COUNT) {
    const err = new Error('ITENS_INCOMPLETOS');
    err.code = 'ITENS_INCOMPLETOS';
    throw err;
  }
  const payload = certificatePayload({
    learner: store.learner,
    course,
    score,
    issuedAt,
    kind: 'course',
  });
  const rec = store.records[course.id];
  rec.status = 'passed';
  rec.passed = true;
  rec.score = score.correct;
  rec.certificate = payload;
  rec.passedAt = payload.issuedAt;
  rec.history.push(payload);
  store.events.push({
    who: { name: store.learner.name, email: store.learner.email },
    when: payload.issuedAt,
    what: course.id,
    pass: true,
    score: score.correct,
  });
  const idx = COURSE_IDS.indexOf(course.id);
  if (idx >= 0 && idx < COURSE_IDS.length - 1) {
    const nextId = COURSE_IDS[idx + 1];
    if (store.records[nextId].status === 'locked') {
      store.records[nextId].status = 'available';
    }
  }
  if (course.id === 'rede-nebulosa') {
    issueTrilhaCertificate(store, issuedAt);
  }
  return payload;
}

export function issueTrilhaCertificate(store, issuedAt = new Date()) {
  const allPassed = COURSE_IDS.every((id) => store.records[id].passed);
  if (!allPassed) {
    const err = new Error('TRILHA_INCOMPLETA');
    err.code = 'TRILHA_INCOMPLETA';
    throw err;
  }
  const payload = certificatePayload({
    learner: store.learner,
    course: { id: 'trilha', title: 'Trilha Academia LGPD Altec' },
    score: { correct: ITEM_COUNT, total: ITEM_COUNT },
    issuedAt,
    kind: 'trilha',
  });
  payload.score = COURSE_IDS.reduce(
    (sum, id) => sum + (store.records[id].score || 0),
    0,
  );
  payload.total = ITEM_COUNT * 3;
  payload.scoreLabel = COURSE_IDS.map(
    (id) => `${store.records[id].score}/${ITEM_COUNT}`,
  ).join(' · ');
  store.trilhaCertificate = payload;
  store.events.push({
    who: { name: store.learner.name, email: store.learner.email },
    when: payload.issuedAt,
    what: 'trilha',
    pass: true,
    score: payload.scoreLabel,
  });
  return payload;
}

export function recordFail(store, session, course, at = new Date()) {
  const score = scoreOf(session);
  store.records[course.id].history.push({
    at: at.toISOString(),
    pass: false,
    score: score.correct,
  });
  store.events.push({
    who: { name: store.learner.name, email: store.learner.email },
    when: at.toISOString(),
    what: course.id,
    pass: false,
    score: score.correct,
  });
  return score;
}

export function persist(store, storage) {
  storage.setItem(STORAGE_KEY, JSON.stringify(store));
}

export function load(storage) {
  const raw = storage.getItem(STORAGE_KEY);
  if (!raw) return null;
  return JSON.parse(raw);
}

export function createMemoryStorage(initial = {}) {
  const data = { ...initial };
  return {
    getItem: (k) => (k in data ? data[k] : null),
    setItem: (k, v) => {
      data[k] = String(v);
    },
    removeItem: (k) => {
      delete data[k];
    },
  };
}

export function assertCourseShape(course) {
  if (course.slides.length !== SLIDE_COUNT) {
    throw new Error(`${course.id} tem ${course.slides.length} slides, esperado ${SLIDE_COUNT}`);
  }
  const items = scoredItemsOf(course);
  if (items.length !== ITEM_COUNT) {
    throw new Error(`${course.id} tem ${items.length} itens, esperado ${ITEM_COUNT}`);
  }
  course.slides.forEach((slide, i) => {
    if (slide.n !== i + 1) throw new Error(`${course.id} slide fora de ordem ${slide.n}`);
    if (slide.beat !== beatFor(slide.n)) {
      throw new Error(`${course.id} slide ${slide.n} beat ${slide.beat} ≠ ${beatFor(slide.n)}`);
    }
    if (!slide.title || !slide.teca || !slide.action) {
      throw new Error(`${course.id} slide ${slide.n} incompleto`);
    }
    if (slide.n !== 30 && !slide.speech && !slide.prompt) {
      throw new Error(`${course.id} slide ${slide.n} sem fala`);
    }
  });
  return true;
}
